import { useLocale, type Locale } from "@/hooks/useLocale";
import { cn } from "@/lib/utils";

type Props = {
  className?: string;
  compact?: boolean;
  /** Visual tone. "hero" uses light-on-dark styling for hero banners. */
  tone?: "default" | "hero";
};

const LABELS: Record<Locale, string> = { en: "EN", es: "ES" };
const A11Y: Record<Locale, string> = {
  en: "Switch to English",
  es: "Cambiar a español",
};

const LocaleToggle = ({ className, compact = false, tone = "default" }: Props) => {
  const { locale, setLocale } = useLocale();

  const base = tone === "hero"
    ? "border-white/20 bg-white/5 text-hero-fg"
    : "border-border bg-card text-foreground";
  const activeCls = tone === "hero"
    ? "bg-white/20 text-hero-fg"
    : "bg-primary text-primary-foreground";
  const inactiveCls = tone === "hero"
    ? "text-hero-muted hover:text-hero-fg"
    : "text-muted-foreground hover:text-foreground";

  return (
    <div
      role="group"
      aria-label="Language / Idioma"
      className={cn(
        "inline-flex items-center rounded-full border p-0.5",
        base,
        compact ? "text-[11px]" : "text-xs",
        className,
      )}
    >
      {(["en", "es"] as Locale[]).map((l) => {
        const active = l === locale;
        return (
          <button
            key={l}
            type="button"
            aria-pressed={active}
            aria-label={A11Y[l]}
            onClick={() => setLocale(l)}
            className={cn(
              "rounded-full font-semibold transition-colors",
              compact ? "px-2 py-0.5" : "px-3 py-1",
              active ? activeCls : inactiveCls,
            )}
          >
            {LABELS[l]}
          </button>
        );
      })}
    </div>
  );
};

export default LocaleToggle;